import onetime from "@-/com.npmjs.onetime";
import type {
  TransformerWorklet,
  Selection,
} from "@-/com.npmjs.react-native-transformer-text-input";
import type { PersistentKeyboardModuleEvents } from "./PersistentKeyboard.types.ts";

type EventName = keyof PersistentKeyboardModuleEvents;
type EventListener<E extends EventName> = PersistentKeyboardModuleEvents[E];

export interface PersistentKeyboardWebInputHandle {
  blur(): void;
  focus(): void;
  setText(text: string): void;
  setSelection?(selection: Selection): void;
}

interface PersistentKeyboardWebStub {
  addListener<E extends EventName>(event: E, listener: EventListener<E>): { remove(): void };
  blur(): void;
  focus(): void;
  preserveFocus(): Promise<void>;
  /** Returns the event count assigned to this write — see the native modules' `setText`. */
  setText(text: string, sourceValue?: string | null): number;
  restoreText(text: string, expectedEventCount: number, sourceValue?: string | null): number;
}

const listenerSetByEvent: { [E in EventName]: Set<EventListener<E>> } = {
  onChangeText: new Set(),
  onKeyPress: new Set(),
};

let currentText = "";
let inputHandle: PersistentKeyboardWebInputHandle | null = null;
let activeRegistrationId = 0;

/**
 * Mirrors the native modules' counter so both platforms share one model: bumped only by
 * DOM-originated input and imperative writes that supersede earlier input.
 * The view reads it to drop `text` syncs derived from a stale render.
 */
let nativeEventCount = 0;

export function getNativeEventCount(): number {
  return nativeEventCount;
}

export function registerWebInputHandle(
  handle: PersistentKeyboardWebInputHandle | null,
  registrationId: number,
): void {
  if (handle !== null) {
    activeRegistrationId = registrationId;
    inputHandle = handle;
    handle.setText(currentText);
    handle.setSelection?.(getTransformerState().selection);
    return;
  }
  if (registrationId === activeRegistrationId) {
    inputHandle = null;
  }
}

function focusPersistentKeyboardInputElement(): void {
  if (typeof document === "undefined") {
    return;
  }
  const activeElement = document.activeElement;
  if (
    activeElement instanceof HTMLInputElement &&
    activeElement.classList.contains("persistent-keyboard-input")
  ) {
    return;
  }
  document.querySelector<HTMLInputElement>(".persistent-keyboard-input")?.focus();
}

const getTransformerState = onetime(() => ({
  worklet: null as TransformerWorklet | null,
  owner: null as object | null,
  selection: { start: 0, end: 0 } as Selection,
  sourceValue: undefined as string | undefined,
}));

export function registerTextTransformer(worklet: TransformerWorklet): { remove(): void } {
  const state = getTransformerState();
  const owner = {};
  state.owner = owner;
  state.worklet = worklet;
  return {
    remove() {
      if (state.owner === owner) {
        state.owner = null;
        state.worklet = null;
      }
    },
  };
}

export function syncWebSelection(text: string, selection: Selection): void {
  if (text === currentText) {
    getTransformerState().selection = selection;
  }
}

export function emitChangeText(
  text: string,
  selection: Selection = { start: text.length, end: text.length },
  composing = false,
): void {
  const state = getTransformerState();
  const result = composing
    ? null
    : state.worklet?.({
        value: text,
        previousValue: currentText,
        previousSourceValue: state.sourceValue,
        selection,
        previousSelection: state.selection,
      });
  const transformed = result?.value ?? text;
  const nextSelection = result?.selection ?? selection;
  const sourceValue = composing && text === currentText ? state.sourceValue : result?.sourceValue;
  if (!composing) {
    inputHandle?.setText(transformed);
    inputHandle?.setSelection?.(nextSelection);
  }
  state.selection = nextSelection;
  text = transformed;
  if (text === currentText && sourceValue === state.sourceValue) {
    return;
  }
  currentText = text;
  state.sourceValue = sourceValue;
  nativeEventCount += 1;
  for (const listener of listenerSetByEvent.onChangeText) {
    listener({ eventCount: nativeEventCount, text, ...(sourceValue === undefined ? {} : { sourceValue }) });
  }
}

export function emitKeyPress(key: string): void {
  for (const listener of listenerSetByEvent.onKeyPress) {
    listener({ key });
  }
}

const stub: PersistentKeyboardWebStub = {
  addListener(event, listener) {
    listenerSetByEvent[event].add(listener);
    return {
      remove() {
        listenerSetByEvent[event].delete(listener);
      },
    };
  },
  blur() {
    inputHandle?.blur();
  },
  focus() {
    inputHandle?.focus();
    focusPersistentKeyboardInputElement();
  },
  preserveFocus() {
    inputHandle?.focus();
    focusPersistentKeyboardInputElement();
    return Promise.resolve();
  },
  restoreText(text, expectedEventCount, source = undefined) {
    const sourceValue = source ?? undefined;
    if (nativeEventCount === expectedEventCount && currentText === text && getTransformerState().sourceValue === sourceValue) {
      inputHandle?.setText(text);
      return nativeEventCount;
    }
    // A different owner may have changed the buffer and then restored the same text.
    // Publish the new revision even in that case, so the returning card catches up.
    getTransformerState().selection = { start: text.length, end: text.length };
    getTransformerState().sourceValue = sourceValue;
    currentText = text;
    nativeEventCount += 1;
    inputHandle?.setText(text);
    for (const listener of listenerSetByEvent.onChangeText) {
      listener({ eventCount: nativeEventCount, text, ...(sourceValue === undefined ? {} : { sourceValue }) });
    }
    return nativeEventCount;
  },
  setText(text, source = undefined) {
    const sourceValue = source ?? undefined;
    if (text === currentText && getTransformerState().sourceValue === sourceValue) {
      // Still report the current count so callers can commit a coherent (text, count) pair.
      return nativeEventCount;
    }
    getTransformerState().selection = { start: text.length, end: text.length };
    getTransformerState().sourceValue = sourceValue;
    currentText = text;
    // Advances the counter for the same reason the native modules do: an imperative write
    // supersedes everything typed before it, so it takes a slot in the same total order as
    // typed changes. Broadcasting it without a count would pair new text with a stale count
    // and make the view's guard reject the matching `text` prop write.
    nativeEventCount += 1;
    inputHandle?.setText(text);
    for (const listener of listenerSetByEvent.onChangeText) {
      listener({ eventCount: nativeEventCount, text, ...(sourceValue === undefined ? {} : { sourceValue }) });
    }
    return nativeEventCount;
  },
};

export const NativeModule = stub;
